import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useI18n, labelOf } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";

export default function CategoryMultiStep({ categories, selected, onToggle, onContinue }) {
  const { lang, t } = useI18n();

  return (
    <div className="pb-24">
      <p className="overline" data-testid="category-overline">{t("step.category")}</p>
      <h2 className="editorial-h text-3xl sm:text-5xl mt-2 text-brand-dark">{t("category.title")}</h2>
      <p className="font-sans text-sm sm:text-base text-brand-muted mt-2 leading-snug">{t("category.subtitle")}</p>
      <p className="font-sans text-[10px] tracking-widest uppercase text-brand-primary mt-1">— {t("category.multiselect")}</p>
      <div className="mt-4 grid grid-cols-2 gap-2.5" data-testid="category-grid">
        {categories.map((c) => {
          const isSelected = selected.includes(c.id);
          return (
            <motion.button
              key={c.id}
              type="button"
              whileTap={{ scale: 0.96 }}
              whileHover={{ y: -2 }}
              onClick={() => onToggle(c.id)}
              data-testid={`category-${c.slug}`}
              className={`relative overflow-hidden rounded-2xl text-left border-2 transition-all aspect-[3/4] ${
                isSelected ? "border-brand-primary shadow-lg" : "border-transparent hover:border-brand-line"
              }`}
            >
              {/* Image */}
              {c.image_url && (
                <img
                  src={c.image_url}
                  alt={labelOf(c.labels, lang)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/45 to-transparent" />
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center shadow-lg" data-testid={`category-check-${c.slug}`}>
                  <Check strokeWidth={3} className="w-4 h-4 text-brand-bone" />
                </div>
              )}
              {/* Content */}
              <div className="absolute inset-x-0 bottom-0 p-3 text-brand-bone">
                <p className="font-display text-lg sm:text-2xl leading-tight font-semibold" style={{ letterSpacing: "-0.02em" }}>{labelOf(c.labels, lang, c.slug)}</p>
                {c.tagline && (
                  <p className="font-sans text-[10px] sm:text-xs text-brand-bone/85 mt-1 leading-tight line-clamp-2">{labelOf(c.tagline, lang, "")}</p>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-[10000] bg-white border-t border-brand-line shadow-[0_-8px_24px_rgba(42,36,33,0.06)]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <p className="font-sans text-xs uppercase tracking-widest text-brand-muted">
            {selected.length === 0 ? t("category.pickone") : `${selected.length} ${t("category.selected")}`}
          </p>
          <Button
            type="button"
            onClick={onContinue}
            disabled={selected.length === 0}
            data-testid="category-continue-btn"
            className="h-12 px-7 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-brand-bone font-semibold disabled:opacity-40"
          >
            {t("common.continue")} →
          </Button>
        </div>
      </div>
    </div>
  );
}
